import React, { useState, useEffect, useCallback } from "react";
import { adminService } from "../../services/admin.service";
import "./AdminRestockRequests.css";

const AdminRestockRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all', 'pending', 'notified'
  const [actionLoading, setActionLoading] = useState(null);

  // 1. Wrap the function in useCallback and give it the 'filter' dependency
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getAllRestockRequests(filter);
      if (res.success) {
        setRequests(res.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filter]); // <-- filter goes here now

  // 2. Put fetchRequests inside the useEffect dependency array
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleMarkAsNotified = async (id) => {
    setActionLoading(id);
    try {
      const res = await adminService.updateRestockStatus(id, "notified");
      if (res.success) {
        // Update local state to reflect the change instantly
        setRequests((prev) =>
          prev.map((req) =>
            req.request_id === id ? { ...req, status: "notified" } : req,
          ),
        );
      }
    } catch (error) {
      alert("Failed to update status.");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="admin-restock-page">
      <div className="admin-page-header">
        <h2>Restock Requests</h2>
        <p>Manage customer notifications for out-of-stock inventory.</p>
      </div>

      <div className="admin-card">
        {/* Filter Controls */}
        <div className="restock-controls">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All Requests
            </button>
            <button
              className={`filter-tab ${filter === "pending" ? "active" : ""}`}
              onClick={() => setFilter("pending")}
            >
              Pending
            </button>
            <button
              className={`filter-tab ${filter === "notified" ? "active" : ""}`}
              onClick={() => setFilter("notified")}
            >
              Notified
            </button>
          </div>
          <div className="request-stats">
            <strong>{requests.length}</strong>{" "}
            {filter === "all" ? "Total" : filter} requests
          </div>
        </div>

        {/* Data Table */}
        <div className="table-responsive">
          {loading ? (
            <div className="restock-loader">Loading requests...</div>
          ) : requests.length === 0 ? (
            <div className="no-requests-empty">
              <span className="empty-icon">📦</span>
              <p>No restock requests found for this filter.</p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product Details</th>
                  <th>Customer Email</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req.request_id}>
                    <td>
                      {new Date(req.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td>
                      <div className="product-info-cell">
                        <strong>{req.product_name || "Unknown Product"}</strong>
                        <span className="product-id-sub">{req.product_id}</span>
                      </div>
                    </td>
                    <td>
                      <a href={`mailto:${req.email}`} className="email-link">
                        {req.email}
                      </a>
                    </td>
                    <td>
                      <span className={`status-badge status-${req.status}`}>
                        {req.status.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {req.status === "pending" ? (
                        <button
                          className="notify-action-btn"
                          disabled={actionLoading === req.request_id}
                          onClick={() => handleMarkAsNotified(req.request_id)}
                        >
                          {actionLoading === req.request_id
                            ? "Updating..."
                            : "Mark Notified"}
                        </button>
                      ) : (
                        <span className="action-completed-text">
                          ✓ Completed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRestockRequests;
